import React from "react";
import {
  Box,
  Button,
  HStack,
  Image,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import {
  AppPhoneCountry,
  AppPhoneCountryCode,
  COUNTRY_OPTIONS as PHONE_COUNTRY_OPTIONS,
} from "./phoneCountries";
// Backward-compatible exports used throughout the existing application.
export type { AppPhoneCountry, AppPhoneCountryCode };
export const COUNTRY_OPTIONS: AppPhoneCountry[] = PHONE_COUNTRY_OPTIONS;
export const getPhoneCountryOptions = (): AppPhoneCountry[] => COUNTRY_OPTIONS;
export const stripToDigits = (value: string) =>
  String(value || "").replace(/[^0-9]/g, "");
export const getPhoneCountryByCode = (
  countryCode?: string,
): AppPhoneCountry => {
  const normalizedCode = String(countryCode || "")
    .trim()
    .toUpperCase();
  return (
    COUNTRY_OPTIONS.find((country) => country.code === normalizedCode) ||
    COUNTRY_OPTIONS[0]
  );
};
export const getPhoneCountryByDialCode = (
  dialCode?: string,
): AppPhoneCountry => {
  const cleanDialCode = stripToDigits(dialCode || "");
  if (cleanDialCode === "265") {
    return getPhoneCountryByCode("MW");
  }
  if (cleanDialCode === "27") {
    return getPhoneCountryByCode("ZA");
  }
  return (
    COUNTRY_OPTIONS.find((country) => country.dialCode === cleanDialCode) ||
    COUNTRY_OPTIONS[0]
  );
};
/**
 * These rules intentionally mirror Login.tsx.
 *
 * South Africa:
 *   - 9 national digits after removing a leading 0
 *   - must start with 6, 7 or 8
 *
 * Malawi:
 *   - 8 or 9 national digits after removing a leading 0
 *   - must start with 1, 8 or 9
 */
export const getPhoneValidationError = (
  countryCode: string,
  phoneNumber: string,
): string => {
  const country = getPhoneCountryByCode(countryCode);
  const digits = stripToDigits(phoneNumber);
  const localDigits = digits.replace(/^0+/, "");
  if (!digits) {
    return "Phone number is required.";
  }
  if (country.code === "ZA") {
    if (localDigits.length !== 9 || !/^[6-8][0-9]{8}$/.test(localDigits)) {
      return "Enter a valid South African phone number.";
    }
    return "";
  }
  if (country.code === "MW") {
    if (
      localDigits.length < 8 ||
      localDigits.length > 9 ||
      !/^[189][0-9]{7,8}$/.test(localDigits)
    ) {
      return "Enter a valid Malawi phone number.";
    }
    return "";
  }
  // Safe fallback if more countries are added later.
  if (localDigits.length < 7 || localDigits.length > 15) {
    return `Enter a valid ${country.label} phone number.`;
  }
  return "";
};
/**
 * Same typing limit used by Login.tsx:
 * - leading 0 => maximum 10 typed digits
 * - no leading 0 => maximum 9 typed digits
 *
 * MW and ZA use exactly this behaviour. A conservative international fallback
 * is retained for any future country option.
 */
export const limitPhoneLocalNumber = (
  countryCode: string,
  rawValue: string,
): string => {
  const country = getPhoneCountryByCode(countryCode);
  const digits = stripToDigits(rawValue);
  if (!digits) return "";
  if (country.code === "MW" || country.code === "ZA") {
    const maxLength = digits.startsWith("0") ? 10 : 9;
    return digits.slice(0, maxLength);
  }
  const maxNationalLength = Math.max(7, 15 - country.dialCode.length);
  const maxLength = digits.startsWith("0")
    ? maxNationalLength + 1
    : maxNationalLength;
  return digits.slice(0, maxLength);
};
export const getPhoneLocalInputMaxLength = (
  countryCode: string,
  currentValue = "",
): number => {
  const country = getPhoneCountryByCode(countryCode);
  const digits = stripToDigits(currentValue);
  if (country.code === "MW" || country.code === "ZA") {
    // Exactly the same cap used on Login.tsx.
    return digits.startsWith("0") ? 10 : 9;
  }
  const maxNationalLength = Math.max(7, 15 - country.dialCode.length);
  return digits.startsWith("0") ? maxNationalLength + 1 : maxNationalLength;
};
export const detectCountryFromNumber = (raw: string): AppPhoneCountry => {
  let digits = stripToDigits(raw);
  if (digits.startsWith("00")) {
    digits = digits.slice(2);
  }
  if (digits.startsWith("265")) {
    return getPhoneCountryByCode("MW");
  }
  if (digits.startsWith("27")) {
    return getPhoneCountryByCode("ZA");
  }
  return COUNTRY_OPTIONS[0];
};
export const splitPhoneNumber = (raw: string) => {
  let digits = stripToDigits(raw);
  if (digits.startsWith("00")) {
    digits = digits.slice(2);
  }
  const country = detectCountryFromNumber(digits);
  if (!digits) {
    return {
      countryCode: country.code,
      localNumber: "",
    };
  }
  if (digits.startsWith(country.dialCode)) {
    return {
      countryCode: country.code,
      localNumber: limitPhoneLocalNumber(
        country.code,
        digits.slice(country.dialCode.length),
      ),
    };
  }
  return {
    countryCode: country.code,
    localNumber: limitPhoneLocalNumber(country.code, digits),
  };
};
export const buildInternationalNumber = (
  countryCode: string,
  localNumber: string,
) => {
  const country = getPhoneCountryByCode(countryCode);
  let cleanPhone = stripToDigits(localNumber).replace(/^0+/, "");
  if (!cleanPhone) return "";
  // Support existing data that may already contain the selected dial code.
  if (cleanPhone.startsWith(country.dialCode)) {
    return cleanPhone;
  }
  return `${country.dialCode}${cleanPhone}`;
};
export type PhoneValidationResult = {
  isValid: boolean;
  error: string;
  internationalNumber: string;
  nationalNumber: string;
};
export const validatePhoneNumber = (
  countryCode: string,
  phoneNumber: string,
): PhoneValidationResult => {
  const country = getPhoneCountryByCode(countryCode);
  const digits = stripToDigits(phoneNumber);
  const nationalNumber = digits.replace(/^0+/, "");
  const error = getPhoneValidationError(country.code, digits);
  if (error) {
    return {
      isValid: false,
      error,
      internationalNumber: "",
      nationalNumber,
    };
  }
  return {
    isValid: true,
    error: "",
    internationalNumber: `${country.dialCode}${nationalNumber}`,
    nationalNumber,
  };
};
export const getDialCodeWithPlus = (countryCode: AppPhoneCountryCode) => {
  const country = getPhoneCountryByCode(countryCode);
  return `+${country.dialCode}`;
};
export const mapDialCodeToCountryCode = (value: string): AppPhoneCountryCode =>
  getPhoneCountryByDialCode(value).code;
export const mapCountryCodeToDialCode = (value: AppPhoneCountryCode): string =>
  getDialCodeWithPlus(value);
type AppPhoneInputProps = {
  label?: string;
  value: string;
  countryCode: string;
  placeholder: string;
  onCountryChange: (value: AppPhoneCountryCode) => void;
  onNumberChange: (value: string) => void;
  theme: any;
  isRequired?: boolean;
  labelColor?: string;
  inputStyles?: Record<string, any>;
  isInvalid?: boolean;
  onBlur?: () => void;
  isDisabled?: boolean;
};
const cardsBorderRadii = "8px";
const AppPhoneInput: React.FC<AppPhoneInputProps> = ({
  label,
  value,
  countryCode,
  placeholder,
  onCountryChange,
  onNumberChange,
  theme,
  isRequired = false,
  labelColor,
  inputStyles,
  isInvalid = false,
  onBlur,
  isDisabled = false,
}) => {
  const selectedCountry = getPhoneCountryByCode(countryCode);
  const constrainedValue = limitPhoneLocalNumber(selectedCountry.code, value);
  const invalidColor = theme.error || theme.danger || "#E53E3E";
  const normalBorder = theme.inputBorder || theme.border;
  const focusColor = isInvalid
    ? invalidColor
    : theme.inputBorderFocus || theme.primary || "#3182ce";
  const handleNumberInputChange = (rawValue: string) => {
    const rawText = String(rawValue || "").trim();
    let digits = stripToDigits(rawText);
    // Pasting +265 / 00265 or +27 / 0027 is allowed. We normalize it into
    // the same local value Login expects and switch the country selector.
    const hadInternationalPrefix =
      rawText.indexOf("+") === 0 || rawText.indexOf("00") === 0;
    if (digits.startsWith("00")) {
      digits = digits.slice(2);
    }
    if (hadInternationalPrefix && digits.startsWith("265")) {
      onCountryChange(getPhoneCountryByCode("MW").code);
      onNumberChange(limitPhoneLocalNumber("MW", digits.slice("265".length)));
      return;
    }
    if (hadInternationalPrefix && digits.startsWith("27")) {
      onCountryChange(getPhoneCountryByCode("ZA").code);
      onNumberChange(limitPhoneLocalNumber("ZA", digits.slice("27".length)));
      return;
    }
    onNumberChange(limitPhoneLocalNumber(selectedCountry.code, digits));
  };
  // Login clears the phone whenever the country changes. Keep the exact same
  // behaviour here so a number entered for one country cannot be carried into
  // another country's validation accidentally.
  const handleCountrySelection = (nextCode: AppPhoneCountryCode) => {
    onCountryChange(nextCode);
    onNumberChange("");
  };
  return (
    <Box>
      {label && (
        <Text
          mb={2}
          fontSize="sm"
          fontWeight="600"
          color={labelColor || theme.text}
        >
          {label}
          {isRequired && (
            <Text as="span" color="red.500">
              {" *"}
            </Text>
          )}
        </Text>
      )}
      <HStack
        spacing={0}
        border={`1px solid ${isInvalid ? invalidColor : normalBorder}`}
        borderRadius="6px"
        overflow="hidden"
        bg={theme.inputBg || theme.surface}
        h="40px"
        transition="all 0.2s ease"
        opacity={isDisabled ? 0.6 : 1}
        _focusWithin={{
          borderColor: focusColor,
          boxShadow: `0 0 0 1px ${focusColor}`,
        }}
      >
        <Menu placement="bottom-start">
          <MenuButton
            as={Button}
            rightIcon={<ChevronDownIcon />}
            variant="ghost"
            borderRadius="0"
            h="40px"
            minW="86px"
            px={3}
            borderRight={`1px solid ${theme.border}`}
            bg={theme.cardBg || theme.background}
            _hover={{ bg: theme.cardBg || theme.background }}
            _active={{ bg: theme.cardBg || theme.background }}
            color={theme.text}
            isDisabled={isDisabled}
            aria-label={`Country code +${selectedCountry.dialCode}`}
          >
            <HStack spacing={1} justify="center" w="full">
              <Image
                src={selectedCountry.flagSrc}
                alt={selectedCountry.label}
                boxSize="18px"
                borderRadius={cardsBorderRadii}
                objectFit="cover"
              />
              <Text fontSize="xs" fontWeight="700">
                +{selectedCountry.dialCode}
              </Text>
            </HStack>
          </MenuButton>
          <MenuList
            p={2}
            minW="260px"
            maxH="320px"
            overflowY="auto"
            borderRadius={cardsBorderRadii}
            border={`1px solid ${theme.border}`}
            bg={theme.surface}
            boxShadow={theme.shadowSm}
            zIndex={9999}
          >
            {getPhoneCountryOptions().map((country) => {
              const isSelected = country.code === selectedCountry.code;
              return (
                <MenuItem
                  key={`${country.code}-${country.label}-${country.dialCode}`}
                  onClick={() => handleCountrySelection(country.code)}
                  borderRadius={cardsBorderRadii}
                  px={3}
                  py={3}
                  mb={1}
                  bg={
                    isSelected
                      ? theme.cardBg || theme.background
                      : "transparent"
                  }
                  _hover={{ bg: theme.cardBg || theme.background }}
                  _focus={{ bg: theme.cardBg || theme.background }}
                >
                  <HStack
                    justify="space-between"
                    align="center"
                    w="full"
                    spacing={3}
                  >
                    <HStack spacing={3} align="center" flex="1" minW={0}>
                      <Image
                        src={country.flagSrc}
                        alt={country.label}
                        boxSize="18px"
                        borderRadius={cardsBorderRadii}
                        objectFit="cover"
                        flexShrink={0}
                      />
                      <Text
                        fontSize="sm"
                        fontWeight="700"
                        color={theme.text}
                        whiteSpace="nowrap"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        lineHeight="1.2"
                      >
                        {country.label} +{country.dialCode}
                      </Text>
                    </HStack>
                    {isSelected && (
                      <Box
                        w="8px"
                        h="8px"
                        borderRadius={cardsBorderRadii}
                        bg={theme.primary || "blue.500"}
                        flexShrink={0}
                      />
                    )}
                  </HStack>
                </MenuItem>
              );
            })}
          </MenuList>
        </Menu>
        <Input
          flex="1"
          variant="unstyled"
          px={4}
          h="40px"
          type="tel"
          inputMode="numeric"
          autoComplete="tel"
          placeholder={placeholder}
          value={constrainedValue}
          maxLength={getPhoneLocalInputMaxLength(
            selectedCountry.code,
            constrainedValue,
          )}
          onInput={(event) => {
            const limitedValue = limitPhoneLocalNumber(
              selectedCountry.code,
              event.currentTarget.value,
            );
            if (event.currentTarget.value !== limitedValue) {
              event.currentTarget.value = limitedValue;
            }
          }}
          onChange={(event) => handleNumberInputChange(event.target.value)}
          onBlur={onBlur}
          color={theme.text}
          fontSize="sm"
          isDisabled={isDisabled}
          aria-invalid={isInvalid || undefined}
          aria-required={isRequired || undefined}
          sx={inputStyles}
        />
      </HStack>
    </Box>
  );
};
export default AppPhoneInput;
